import { useState, type ReactElement } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  ChevronRight,
  Info,
  Plus,
  Search,
  Users,
} from "lucide-react";
import {
  Alert,
  AsyncSection,
  asyncStatus,
  Button,
  Card,
  DataTable,
  EmptyState,
  Input,
  Modal,
  SectionHeader,
  SettingsDetailsButton,
  SettingsMetaCard,
  SettingsPageLayout,
  TabPanel,
  Tabs,
  Textarea,
  TruncatedText,
  type DataTableColumn,
} from "@playbookai/ui";
import type { Team } from "../../api/teams-api.js";
import { getDocumentationUrl } from "../../config/runtime.js";
import {
  useCreateTeam,
  useRequestToJoinTeam,
  useTeamList,
} from "./useTeams.js";
import {
  TeamRequestsPanel,
  useTeamRequestQueues,
} from "./TeamRequestsPanel.js";
import { LoadMore } from "./LoadMore.js";
import { TeamTile, TeamViewerStatus, tabLabel } from "./team-identity.js";

/**
 * Shared Teams — the workspace destination (ADR-0082), drawn as a Settings page.
 *
 * THE SETTINGS FRAME, NOT ITS OWN. Teams sits in the Settings rail's
 * Collaboration group, so it takes the same header, lane and meta aside every
 * sibling does (`SectionHeader variant="settings"` + `SettingsPageLayout`). It
 * used to draw its own centred `max-w-6xl` column inside the Settings lane,
 * which put its title, width and gutters somewhere no other page in the rail
 * put them.
 *
 * THREE VIEWS, one route: **My teams**, **Explore teams**, and
 * **Requests & invitations**, as the design system's content `Tabs` — the same
 * control Player AI uses for its sub-views. They stay one route with a `?view=`
 * because a member moves between them constantly — accept an invitation, see
 * it in My teams, go looking for the next one — and three URLs would make that
 * three navigations with three loading states.
 *
 * ONE ACCENT (DS invariant §9): the header's Create team. Row actions — Ask to
 * join, Answer invitation — are never accented, and an empty state that offers
 * the same create again offers it `soft`.
 *
 * NOT ADMIN CHROME. This lives at `/teams`, outside `/admin`, because any active
 * member creates a team and nothing here is an administrator's business. The one
 * administrator power (recovering ownership of a team whose owners are gone)
 * lives on the team's own page and is gated server-side.
 *
 * THE ONE THING THIS SCREEN MUST NOT DO is show a pending person as a member.
 * Every row reads `viewerRole` and `viewerPendingKind` as separate facts
 * (`TeamViewerStatus`), and the row action is derived from both — never from
 * `memberCount`.
 */

type TeamsView = "mine" | "explore" | "requests";

function isView(value: string | null): value is TeamsView {
  return value === "mine" || value === "explore" || value === "requests";
}

export function TeamsPage(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get("view");
  const view: TeamsView = isView(rawView) ? rawView : "mine";
  const query = searchParams.get("q") ?? "";
  const [creating, setCreating] = useState(
    () => searchParams.get("action") === "create",
  );

  /* THE COUNTS ARE THE UNFILTERED TOTALS, read from the same cached queries the
     lists use when the search is empty — so a search narrows the table and the
     "N of M shown" line, never the tab. Undefined until the first page lands:
     a "0" before then would be a false statement about the workspace. */
  const mine = useTeamList("mine", "");
  const explore = useTeamList("explore", "");
  const requests = useTeamRequestQueues();
  const waiting = requests.decide.length;

  const setParam = (key: string, value: string): void => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value) params.set(key, value);
        else params.delete(key);
        return params;
      },
      { replace: true },
    );
  };
  const showView = (next: TeamsView): void => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "mine") params.delete("view");
        else params.set("view", next);
        /* A search belongs to the list it was typed into. Carrying "quality"
           from Explore into My teams would filter a list the reader has not
           searched yet. */
        params.delete("q");
        return params;
      },
      { replace: true },
    );
  };

  const documentationUrl = getDocumentationUrl();

  const meta = (
    <SettingsMetaCard
      icon={Info}
      title="About teams"
      description="A team is a list of people, not a permission. Use one to send work to the same group again, such as a LAMA campaign audience."
      rows={[
        { label: "Who can create", value: "Any member" },
        { label: "How people join", value: "Invitation or approval" },
        { label: "Access it grants", value: "None" },
      ]}
      {...(documentationUrl
        ? {
            links: [
              {
                label: "How teams work",
                icon: BookOpen,
                href: documentationUrl,
                external: true,
                accessory: <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />,
              },
            ],
          }
        : {})}
    />
  );

  return (
    <div className="pb-motion-content-enter">
      <SectionHeader
        variant="settings"
        leading={<Users className="w-4 h-4 text-pb-cyan-text" />}
        title="Teams"
        description="Reusable groups of the people you work with. Being on a team changes nothing about what you can access."
        actionsSlot={
          <div className="flex shrink-0 items-center gap-2">
            <SettingsDetailsButton meta={meta} />
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Create team
            </Button>
          </div>
        }
      />
      <SettingsPageLayout mainWidth="fluid" meta={meta}>
        <Tabs
          aria-label="Teams view"
          items={[
            { value: "mine", label: tabLabel("My teams", mine.totalCount) },
            {
              value: "explore",
              label: tabLabel("Explore teams", explore.totalCount),
            },
            {
              value: "requests",
              /* Only a count the reader has to ACT on, and only once it is
                 more than nothing — a "0" on an inbox is noise. "3+" when the
                 queues have pages not yet loaded, because the loaded pages are
                 all this screen can count. */
              label: tabLabel(
                "Requests & invitations",
                waiting > 0
                  ? `${waiting}${requests.morePages ? "+" : ""}`
                  : undefined,
                true,
              ),
            },
          ]}
          value={view}
          onValueChange={(next) => {
            if (isView(next)) showView(next);
          }}
        >
          <TabPanel value={view} className="pt-4.5">
            {view === "requests" ? (
              <TeamRequestsPanel />
            ) : (
              <TeamListView
                /* One list per scope. The lists keep their previous rows while
                   a new SEARCH loads; keyed by scope, a tab switch starts
                   fresh instead of showing My teams under Explore. */
                key={view}
                view={view}
                query={query}
                onQueryChange={(next) => setParam("q", next)}
                onCreate={() => setCreating(true)}
                onExplore={() => showView("explore")}
                onAnswer={() => showView("requests")}
              />
            )}
          </TabPanel>
        </Tabs>
      </SettingsPageLayout>

      <CreateTeamDialog
        open={creating}
        onClose={() => {
          setCreating(false);
          if (searchParams.get("action") === "create") setParam("action", "");
        }}
      />
    </div>
  );
}

function TeamListView({
  view,
  query,
  onQueryChange,
  onCreate,
  onExplore,
  onAnswer,
}: {
  view: "mine" | "explore";
  query: string;
  onQueryChange: (next: string) => void;
  onCreate: () => void;
  onExplore: () => void;
  onAnswer: () => void;
}): ReactElement {
  const navigate = useNavigate();
  /* THE SEARCH IS PART OF THE QUERY KEY, which is what makes typing RESET the
     page set rather than append to it: `useTeamList` keys on (scope, query), so
     page 2 of "quality" can never be concatenated onto page 1 of "qual". */
  const teams = useTeamList(view, query);
  const items = teams.items;
  /* A refused request per team, so two requests in flight — or a refusal for
     one team while another is still sending — each report on their own row's
     team. Keyed by team id; a new attempt on that row clears its entry. */
  const [joinFailures, setJoinFailures] = useState<
    Readonly<Record<string, { name: string; message: string }>>
  >({});
  const recordJoin = (team: Team, error: Error | null): void =>
    setJoinFailures((prev) => {
      const next = { ...prev };
      if (error)
        next[team.teamId] = { name: team.name, message: error.message };
      else delete next[team.teamId];
      return next;
    });

  const columns: ReadonlyArray<DataTableColumn<Team>> = [
    {
      id: "team",
      /* Column floors (PB-5436): Team takes what is left with a 176px floor,
         and the fixed columns count their own widths, so a box under 656px
         (the Player AI rail at 1024-1152px) scrolls instead of squeezing the
         Team column to nothing; wider boxes are unchanged. */
      minWidth: 176,
      header: "Team",
      cardRole: "title",
      cell: (team) => (
        <div className="flex min-w-0 items-center gap-3">
          <TeamTile name={team.name} />
          <div className="min-w-0">
            {/* The router link keeps today's look (Link has no variant that
                is pb-text at rest and cyan and underlined on hover); a
                clipped name or description shows in the design-system
                Tooltip through TruncatedText (d1), not a native title. */}
            <Link
              to={`/teams/${team.teamId}`}
              className="block font-medium text-pb-text hover:text-pb-accent-text hover:underline focus-ring rounded-sm underline-offset-2"
            >
              <TruncatedText>{team.name}</TruncatedText>
            </Link>
            {/* Team names are NOT unique, so the description is how two teams
                called "Quality" are told apart. */}
            {team.description ? (
              <p className="text-caption text-pb-text-muted">
                <TruncatedText>{team.description}</TruncatedText>
              </p>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      id: "members",
      minWidth: 136,
      header: "Members",
      width: "8.5rem",
      cell: (team) => (
        <span className="inline-flex items-center gap-1.5 tabular-nums text-pb-text-secondary">
          <Users
            className="h-3.5 w-3.5 shrink-0 text-pb-text-muted"
            aria-hidden="true"
          />
          {team.memberCount === 1 ? "1 member" : `${team.memberCount} members`}
        </span>
      ),
    },
    {
      id: "status",
      minWidth: 168,
      header: "Your status",
      width: "10.5rem",
      cardRole: "badge",
      cell: (team) => <TeamViewerStatus team={team} />,
    },
    {
      id: "actions",
      minWidth: 176,
      header: <span className="sr-only">Actions</span>,
      width: "11rem",
      align: "end",
      cardRole: "actions",
      cell: (team) => {
        const archived = team.archivedAt !== null;
        return (
          <div className="flex items-center justify-end gap-2">
            {/* Offered only when there is nothing outstanding: an invitation is
                answered on the Requests view, and a second request is refused
                by the server anyway. */}
            {!archived &&
            team.viewerRole === null &&
            team.viewerPendingKind === null ? (
              <JoinButton team={team} onResult={recordJoin} />
            ) : null}
            {!archived && team.viewerPendingKind === "invitation" ? (
              <Button size="sm" variant="ghost" onClick={onAnswer}>
                Answer invitation
              </Button>
            ) : null}
            <ChevronRight
              className="h-4 w-4 shrink-0 text-pb-text-muted max-md:hidden"
              aria-hidden="true"
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-sm">
          <Input
            aria-label="Search teams"
            placeholder="Search teams by name"
            value={query}
            prefix={<Search className="h-4 w-4" aria-hidden="true" />}
            onChange={(event) => onQueryChange(event.target.value)}
          />
        </div>
        {items.length > 0 ? (
          <p className="ms-auto text-body-small tabular-nums text-pb-text-muted">
            {/* SHOWN of MATCHING, and the second number is the server's
                windowed `count(*) OVER ()` rather than the rows fetched. Saying
                "12 teams" when 400 match is the misreport a page control exists
                to prevent. */}
            {`${items.length} of ${teams.totalCount ?? items.length} shown`}
          </p>
        ) : null}
      </div>

      {Object.entries(joinFailures).map(([teamId, failure]) => (
        <Alert
          key={teamId}
          role="status"
          tone="critical"
          title={`Your request to join ${failure.name} did not go through`}
        >
          {failure.message}
        </Alert>
      ))}

      <AsyncSection
        status={asyncStatus({
          isLoading: teams.isLoading,
          isError: teams.error !== null,
          isEmpty: teams.isEmpty,
          hasData: items.length > 0,
        })}
        errorTitle={"Teams could not be loaded"}
        errorDescription={
          teams.error instanceof Error
            ? teams.error.message
            : "The workspace's teams are temporarily unavailable."
        }
        onRetry={teams.refetch}
        loadingLabel={view === "mine" ? "your teams" : "the workspace's teams"}
        /* The table's own loading rows, so the region has the shape of what is
           coming and does not jump when it lands. */
        skeleton={
          <DataTable
            columns={columns}
            data={[]}
            getRowId={(team) => team.teamId}
            layout="fixed"
            loading
            loadingRows={4}
          />
        }
        empty={
          <TeamListEmpty
            view={view}
            query={query}
            onCreate={onCreate}
            onExplore={onExplore}
            onClearSearch={() => onQueryChange("")}
          />
        }
      >
        <>
          <DataTable
            columns={columns}
            data={items}
            getRowId={(team) => team.teamId}
            layout="fixed"
            responsive
            onRowActivate={(team) => navigate(`/teams/${team.teamId}`)}
            caption={
              view === "mine"
                ? "Teams you own or are a member of"
                : "Every team in this workspace"
            }
          />
          <LoadMore
            label="teams"
            hasMore={teams.hasMore}
            isLoadingMore={teams.isLoadingMore}
            failed={teams.loadMoreFailed}
            onLoadMore={teams.loadMore}
          />
        </>
      </AsyncSection>
    </div>
  );
}

/**
 * One row's Ask to join, with its OWN mutation. A single mutation shared by
 * the list tracked only its latest `variables`, so asking to join a second
 * team re-enabled the first while its request was still in flight — and a
 * second click there is refused by the server as a duplicate, which then read
 * as a failure. Each row now knows only about its own request.
 */
function JoinButton({
  team,
  onResult,
}: {
  team: Team;
  onResult: (team: Team, error: Error | null) => void;
}): ReactElement {
  const join = useRequestToJoinTeam();
  return (
    <Button
      size="sm"
      disabled={join.isPending}
      onClick={() => {
        onResult(team, null);
        join.mutate(team.teamId, {
          onError: (error) =>
            onResult(
              team,
              error instanceof Error ? error : new Error("Try again."),
            ),
        });
      }}
    >
      Ask to join
    </Button>
  );
}

/** Same frame as the other Settings empty states: a dashed card, so it does not
 *  hang in a blank half-page. */
/*
  An empty EXPLORE and an empty MY TEAMS are different facts, and a shared "no
  teams" would be wrong for one of them: a workspace can have plenty of teams
  while the reader is on none. A search that matched nothing is a third.
*/
function TeamListEmpty({
  view,
  query,
  onCreate,
  onExplore,
  onClearSearch,
}: {
  view: "mine" | "explore";
  query: string;
  onCreate: () => void;
  onExplore: () => void;
  onClearSearch: () => void;
}): ReactElement {
  /* Each framed empty state is EmptyState on the dashed Card. */
  if (query) {
    return (
      <Card variant="dashed">
        <EmptyState
          variant="no-results"
          icon={<Search className="h-5 w-5" />}
          title="No teams match that search"
          description="Team names are matched anywhere in the name, so try a shorter word."
          action={
            <Button variant="secondary" onClick={onClearSearch}>
              Clear search
            </Button>
          }
        />
      </Card>
    );
  }
  /* `soft`, not primary: the header already offers Create team, accented, a
     few centimetres above — two identical indigo buttons for one action is the
     defect invariant §9 exists to catch. */
  return view === "mine" ? (
    <Card variant="dashed">
      <EmptyState
        variant="first-use"
        icon={<Users className="h-5 w-5" />}
        title="You are not on any team yet"
        description="Explore the workspace's teams to ask to join one, or create your own."
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="secondary" onClick={onExplore}>
              Explore teams
            </Button>
            <Button variant="soft" onClick={onCreate}>
              Create team
            </Button>
          </div>
        }
      />
    </Card>
  ) : (
    <Card variant="dashed">
      <EmptyState
        variant="first-use"
        icon={<Users className="h-5 w-5" />}
        title="This workspace has no teams yet"
        description="Create the first one — anybody can, and it grants nobody any access."
        action={
          <Button variant="soft" onClick={onCreate}>
            Create team
          </Button>
        }
      />
    </Card>
  );
}

/** Mirrors `teams_name_check` / `teams_description_check`. A client that
 *  accepted more would only discover the refusal at the write. */
const NAME_MAX = 120;
const DESCRIPTION_MAX = 500;

function CreateTeamDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const navigate = useNavigate();
  const create = useCreateTeam();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const trimmed = name.trim();
  const submit = (): void => {
    if (!trimmed) return;
    create.mutate(
      { name: trimmed, description: description.trim() || null },
      {
        onSuccess: ({ teamId }) => {
          setName("");
          setDescription("");
          onClose();
          navigate(`/teams/${teamId}`);
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create team"
      description="You become its first owner. Nobody else joins until they accept an invitation or you approve their request."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!trimmed || create.isPending}
          >
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Name"
          value={name}
          maxLength={NAME_MAX}
          onChange={(event) => setName(event.target.value)}
        />
        <Textarea
          label="Description (optional)"
          value={description}
          maxLength={DESCRIPTION_MAX}
          onChange={(event) => setDescription(event.target.value)}
          /* Two teams may legitimately share a name, so this is what tells them
             apart in a picker. Said here rather than left to be discovered. */
          hint="Team names are not unique — a line about what this team is for helps people tell yours apart."
        />
        {create.isError && (
          <Alert role="status" tone="critical" title="The team was not created">
            {create.error instanceof Error
              ? create.error.message
              : "Try again."}
          </Alert>
        )}
      </div>
    </Modal>
  );
}
